import React from 'react';

/**
 * FilePreviewSection Component
 * 75% / 25% layout displaying metadata, description, change notes,
 * and visual preview with action buttons.
 */
export default function FilePreviewSection({
    file,
    onPreviewFullScreen,
    onDownload,
    onPublishToCRE,
}) {
    if (!file) {
        return null;
    }

    const isPdf = file.type === 'PDF';
    const isSpreadsheet = ['XLSX', 'XLS', 'CSV', 'TSV'].includes(file.type);
    const isDesign = ['AI', 'PSD', 'FIG', 'SKETCH', 'XD', 'EPS', 'INDD'].includes(file.type);
    const isArchive = ['ZIP', 'RAR', '7Z', 'TAR'].includes(file.type);

    const getIconColor = () => {
        if (isPdf) return 'text-[#ba1a1a]';
        if (isSpreadsheet) return 'text-[#006a61]';
        if (isDesign) return 'text-[#a259ff]';
        if (isArchive) return 'text-[#6366f1]';
        return 'text-[#0070F3]';
    };

    const isDeliverable = file.folderCode === '05' || file.folderId?.includes('_05');
    const isCRESynced = file.publishedToCRE || !!file.creFileId;

    return (
        <section className="bg-white dark:bg-[#141c2b] p-6 border-b border-[#c1c6d7] dark:border-[#344258]">
            <div className="flex flex-col lg:flex-row gap-8">
                {/* Left Container (75%) */}
                <div className="lg:w-3/4 flex flex-col gap-6">
                    {/* Metadata Section */}
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-[#0b1c30] dark:text-[#eaf1ff]">
                                File Metadata
                            </h3>
                            {isCRESynced && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 shadow-2xs">
                                    <span className="material-symbols-outlined text-sm">cloud_done</span>
                                    CRE Lineage: {file.creFileId || 'Synced'}
                                </span>
                            )}
                        </div>
                        <div className="bg-[#eff4ff] dark:bg-[#1a2538] rounded-xl p-5 border border-[#c1c6d7] dark:border-[#344258]">
                            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4">
                                <div>
                                    <span className="text-xs font-semibold text-[#727786] dark:text-[#9aa5bc] uppercase tracking-wider block mb-1">
                                        File Name
                                    </span>
                                    <span className="text-sm font-bold text-[#0b1c30] dark:text-[#eaf1ff] truncate block" title={file.name}>
                                        {file.name}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-[#727786] dark:text-[#9aa5bc] uppercase tracking-wider block mb-1">
                                        Project
                                    </span>
                                    <span className="text-sm font-bold text-[#0b1c30] dark:text-[#eaf1ff] truncate block">
                                        {file.project || 'General'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-[#727786] dark:text-[#9aa5bc] uppercase tracking-wider block mb-1">
                                        Type
                                    </span>
                                    <span className="text-sm font-bold text-[#0b1c30] dark:text-[#eaf1ff]">
                                        {file.type}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-[#727786] dark:text-[#9aa5bc] uppercase tracking-wider block mb-1">
                                        Size
                                    </span>
                                    <span className="text-sm font-bold text-[#0b1c30] dark:text-[#eaf1ff]">
                                        {file.size}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-[#727786] dark:text-[#9aa5bc] uppercase tracking-wider block mb-1">
                                        Version
                                    </span>
                                    <span className="text-sm font-bold text-[#0070F3]">
                                        {file.version}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-[#727786] dark:text-[#9aa5bc] uppercase tracking-wider block mb-1">
                                        Date
                                    </span>
                                    <span className="text-sm font-bold text-[#0b1c30] dark:text-[#eaf1ff]">
                                        {file.date}
                                    </span>
                                </div>
                                {file.revisionRound && (
                                    <div>
                                        <span className="text-xs font-semibold text-[#727786] dark:text-[#9aa5bc] uppercase tracking-wider block mb-1">
                                            Revision Round
                                        </span>
                                        <span className="text-sm font-bold text-purple-600 dark:text-purple-400">
                                            {file.revisionRound}
                                        </span>
                                    </div>
                                )}
                                {file.subfolderCategory && (
                                    <div>
                                        <span className="text-xs font-semibold text-[#727786] dark:text-[#9aa5bc] uppercase tracking-wider block mb-1">
                                            Category
                                        </span>
                                        <span className="text-sm font-bold text-[#0b1c30] dark:text-[#eaf1ff]">
                                            {file.subfolderCategory}
                                        </span>
                                    </div>
                                )}
                                {file.publishedAt && (
                                    <div>
                                        <span className="text-xs font-semibold text-[#727786] dark:text-[#9aa5bc] uppercase tracking-wider block mb-1">
                                            Published to CRE
                                        </span>
                                        <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                                            {file.publishedAt}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Description and Change Notes (Side-by-Side) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-xs font-bold text-[#0b1c30] dark:text-[#eaf1ff] mb-2 uppercase tracking-wide">
                                Description
                            </label>
                            <textarea
                                className="w-full bg-[#eff4ff] dark:bg-[#1a2538] border border-[#c1c6d7] dark:border-[#344258] rounded-xl p-3.5 text-sm text-[#0b1c30] dark:text-[#eaf1ff] focus:ring-2 focus:ring-[#0070F3] focus:outline-none min-h-[90px] resize-none leading-relaxed"
                                readOnly
                                value={file.description || 'No description provided.'}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-[#0b1c30] dark:text-[#eaf1ff] mb-2 uppercase tracking-wide">
                                Change Notes
                            </label>
                            <textarea
                                className="w-full bg-[#eff4ff] dark:bg-[#1a2538] border border-[#c1c6d7] dark:border-[#344258] rounded-xl p-3.5 text-sm text-[#0b1c30] dark:text-[#eaf1ff] focus:ring-2 focus:ring-[#0070F3] focus:outline-none min-h-[90px] resize-none leading-relaxed"
                                readOnly
                                value={file.changeNotes || 'No change notes documented.'}
                            />
                        </div>
                    </div>
                </div>

                {/* Right Container (25%) */}
                <div className="lg:w-1/4 flex flex-col gap-4">
                    <h3 className="text-lg font-bold text-[#0b1c30] dark:text-[#eaf1ff]">
                        File Preview
                    </h3>

                    {/* Preview Area */}
                    <div
                        className="bg-[#eff4ff] dark:bg-[#1a2538] rounded-xl border border-[#c1c6d7] dark:border-[#344258] flex flex-col items-center justify-center p-6 min-h-[190px] relative group"
                    >
                        <span className={`material-symbols-outlined text-7xl opacity-40 transition-transform group-hover:scale-105 ${getIconColor()}`}>
                            {file.icon || 'description'}
                        </span>
                        <p className="mt-3 text-sm font-semibold text-[#414754] dark:text-[#eaf1ff] text-center truncate max-w-[200px]" title={file.name}>
                            {file.name}
                        </p>
                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-col gap-2 mt-1">
                        {isDeliverable && !isCRESynced && (
                            <button
                                type="button"
                                onClick={() => onPublishToCRE?.(file)}
                                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-sm text-xs font-bold w-full transition-all active:scale-98"
                            >
                                <span className="material-symbols-outlined text-base">publish</span>
                                Publish Deliverable to CRE
                            </button>
                        )}
                        {isCRESynced && (
                            <div className="flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 rounded-lg text-xs font-bold text-center">
                                <span className="material-symbols-outlined text-base">cloud_done</span>
                                Published to CRE ({file.creFileId || 'Synced'})
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => onPreviewFullScreen?.(file)}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0070F3] hover:bg-[#0059c5] text-white rounded-lg shadow-sm text-xs font-semibold w-full transition-all active:scale-98"
                        >
                            <span className="material-symbols-outlined text-base">fullscreen</span>
                            Preview Full Screen
                        </button>
                        <button
                            type="button"
                            onClick={() => onDownload?.(file)}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-[#1a2538] text-[#0b1c30] dark:text-[#eaf1ff] border border-[#c1c6d7] dark:border-[#344258] hover:bg-[#eff4ff] dark:hover:bg-[#212f45] rounded-lg shadow-2xs text-xs font-semibold w-full transition-all active:scale-98"
                        >
                            <span className="material-symbols-outlined text-base">download</span>
                            Download File
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}
